import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "nk_admin_token";

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error("Invalid email or password");
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md">
        <img src="/nk-logo-dark.png" alt="NK Bridal" className="h-12 w-auto mx-auto mb-8 object-contain" />
        <h1 className="font-serif font-light text-4xl text-center tracking-tight mb-2">Enquiry Inbox</h1>
        <p className="text-center text-ink/50 text-sm mb-10 uppercase tracking-[0.25em]">Private · NK Bridal</p>
        <label className="block mb-6">
          <span className="text-[10px] uppercase tracking-[0.3em] text-ink/50">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email"
            className="w-full bg-transparent border-b border-ink py-3 mt-2 text-lg font-light focus:outline-none" />
        </label>
        <label className="block mb-8">
          <span className="text-[10px] uppercase tracking-[0.3em] text-ink/50">Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password"
            className="w-full bg-transparent border-b border-ink py-3 mt-2 text-lg font-light focus:outline-none" />
        </label>
        {error && <p className="text-red-600 text-sm mb-4" data-testid="admin-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="w-full bg-ink text-ivory py-4 text-xs uppercase tracking-[0.25em] hover:opacity-90 disabled:opacity-60">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function Inbox({ onLogout }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API}/api/admin/enquiries`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return onLogout();
      const data = await res.json();
      setItems(data);
    } catch (err) {
      setError("Could not load enquiries.");
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-inbox">
      <header className="border-b border-line sticky top-0 bg-ivory/90 backdrop-blur z-10">
        <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/nk-logo-dark.png" alt="NK" className="h-8 w-auto object-contain" />
            <span className="font-serif text-xl">Enquiry Inbox</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} data-testid="admin-refresh" className="text-xs uppercase tracking-[0.25em] link-underline">Refresh</button>
            <button onClick={onLogout} data-testid="admin-logout" className="text-xs uppercase tracking-[0.25em] border border-ink px-4 py-2 hover:bg-ink hover:text-ivory transition-colors">Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-10">
        {error && <p className="text-red-600">{error}</p>}
        {items === null && <p className="text-ink/50">Loading…</p>}
        {items && items.length === 0 && (
          <p className="text-ink/50 font-serif italic text-2xl mt-10">No enquiries yet.</p>
        )}
        <div className="space-y-4">
          {items && items.map((it) => (
            <div key={it.id} className="border border-line p-6" data-testid="enquiry-row">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h3 className="font-serif text-2xl font-light">{it.name}</h3>
                <span className="text-xs text-ink/60 uppercase tracking-[0.2em]">{fmt(it.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm mb-3">
                <a href={`mailto:${it.email}`} className="link-underline">{it.email}</a>
                {it.phone && <a href={`tel:${it.phone}`} className="link-underline">{it.phone}</a>}
                {it.location && <span className="text-ink/60">Location: {it.location}</span>}
                {it.service && <span className="text-ink/60">Interested in: {it.service}</span>}
                {it.date && <span className="text-ink/60">Appt: {it.date}</span>}
                {it.wedding_date && <span className="text-ink/60">Wedding: {it.wedding_date}</span>}
                {it.referral && <span className="text-ink/60">Via: {it.referral}</span>}
              </div>
              {it.message && <p className="text-ink/70 font-light leading-relaxed">{it.message}</p>}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };
  return authed ? <Inbox onLogout={logout} /> : <Login onAuthed={() => setAuthed(true)} />;
}
